import { AppMode, FilterType } from '@repo/frontend-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// Context
import {
  useNotificationContext,
  useTwinContext,
  useFilterContext,
  useModeContext,
} from '@repo/front-end-state';
// Utils
import { findEntityWithBID, createFilterPath, removeProtocol } from '@repo/front-end-utils';
import { flyToTarget, FlightInterrupted } from '@repo/ui';

// Stores
import {
  useOrganizationStore,
  useTwinStore,
  useTwinModelStore,
  useUserStore,
  useSettingsStore,
  useSpotlightStore,
} from '@repo/front-end-state';

// API
import { getOrgTwins, getOrganisationById } from '@repo/front-end-services';
import type { Twin } from '@repo/front-end-services';

// Config
import Config from '../Config';

// BID helpers
import { extractOrgBidFromSpotlightBid } from './extractOrgBidFromSpotlightBid';
import { chainAfterPredictiveFlight } from './chainAfterPredictiveFlight';

interface UseSpotlightOptions {
  allModelsLoaded: boolean;
  loadingBanterRunning: boolean;
}

/**
 * Custom hook to handle spotlight functionality
 * Selects and zooms to an asset/area when a spotlight BID is fired into the SpotlightStore.
 *
 * Subscribes to `activeSpotlightBid` and `spotlightFireCount` on the store; gates on count
 * change (not BID change) so re-firing the same BID always re-triggers processing.
 *
 * How it works:
 * 1. Extracts the organization BID from the spotlight BID (e.g., TFN-WMZ-E035 -> TFN) and auto-selects it if needed
 * 2. Extracts the twin BID from the spotlight BID (e.g., TFN-WMZ-E035 -> TFN-WMZ) and sets it if needed
 * 3. Once the twin is loaded, finds the target entity by BID and selects it in the asset panel using filters
 * 4. Hands camera framing off to `flyToTarget(entity.bID)`, which does its own coordinate
 *    resolution, viewport-aware framing (cameraForBounds), and animation timing.
 */
export const useSpotlight = ({ allModelsLoaded, loadingBanterRunning }: UseSpotlightOptions) => {
  const spotlightBid = useSpotlightStore(s => s.activeSpotlightBid) ?? undefined;
  const spotlightFireCount = useSpotlightStore(s => s.spotlightFireCount);
  const { twin, twinBId, setTwinBId, setTwin, setTwinNeedsRefresh } = useTwinContext();
  const { setFilter, removeFilterByType, clearFilter } = useFilterContext();
  const { setAppModeWithFilterReset } = useModeContext();
  const { authMetadata, setAuthMetadata } = useUserStore();
  const { showNotification } = useNotificationContext();
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  const { setMetrics } = useSettingsStore();
  const { getAllOrganizations } = useOrganizationStore();
  const { setSelectedTwinBID } = useTwinStore();
  // CP6c: accessibleTwins lifted into TwinModelStore so flyToTarget's
  // resolveTarget can look up the saved camera pose of a not-yet-loaded twin
  // for the predictive cross-twin flight dispatched below.
  const accessibleTwins = useTwinModelStore(s => s.accessibleTwins);
  const setAccessibleTwins = useTwinModelStore(s => s.setAccessibleTwins);
  const setPrefetchedTwinFlightBid = useTwinModelStore(s => s.setPrefetchedTwinFlightBid);
  const spotlightProcessedRef = useRef<boolean>(false);
  const lastSeenFireCountRef = useRef<number>(0);
  // CP6c handoff: the predictive twin flight is dispatched fire-and-forget
  // here; the entity flight (phase 2) awaits its settle so it doesn't
  // interrupt phase 1 mid-flight when the twin loads faster than expected.
  const predictiveTwinFlightPromiseRef = useRef<Promise<void> | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState<boolean>(false);
  const [isSelectingOrg, setIsSelectingOrg] = useState<boolean>(false);
  const lastFetchedOrgBIDRef = useRef<string | undefined>(undefined);

  /**
   * 🚨 ONE FIRE, ONE ATTEMPT — the guard that stops the org auto-select effect retrying forever.
   *
   * THE DEFECT. Peter's review of live staging, 2026-09-02: one click on an Equipment
   * Recommendations fly-to link stacked FIFTEEN identical `Could not load organization EQT:137`
   * toasts. The effect below is an unbounded retry loop by construction, and `isSelectingOrg` is
   * not a brake on it — it is the ACCELERATOR. `isSelectingOrg` is in the dependency array, the
   * `finally` sets it back to `false`, and `authMetadata.orgBID` is only written on the SUCCESS
   * path, so every completed failure re-runs the effect straight back into the same failure.
   * Measured in `useSpotlight.catalogueBid.test.tsx`: **215 reads of `/api/organisations/EQT:137`
   * in a 15 s window from a single fire.**
   *
   * ⚠️ AND IT IS NOT ABOUT BAD BIDS. Any org bid the middleware will not serve reaches this — a
   * `?spotlight=` deep link with a typo, an org the user cannot see, a middleware outage. The
   * storm is WORST exactly when the middleware is already struggling, because a slow round trip is
   * what lets each iteration commit its render and take the next step. On a fast enough connection
   * the loop can accidentally die of a React bailout after two turns; that is luck, not a guard.
   *
   * ⭐ KEYED ON `${orgBid}#${spotlightFireCount}`, NOT ON THE BID ALONE, and the fire count is the
   * load-bearing half. Keying on the bid would make a transient failure permanent for the rest of
   * the session — one network blip and the user could never fly to that organisation again, with
   * no way to ask for a retry. `fireSpotlight` bumps the count on every trigger, so a user
   * RE-CLICKING is a new key and gets a genuine second attempt. One click, one attempt, one toast.
   *
   * ⛔ WRITTEN BEFORE THE FIRST `await`, which is the same discipline and the same reason as
   * `twinFetchInFlightRef` below: a ref written synchronously is already visible to the next effect
   * invocation, whereas `isSelectingOrg` is `useState` read from the RENDER CLOSURE and still reads
   * `false` until a commit lands. So this guard also closes the concurrent-request race that
   * `isSelectingOrg` never could — the one `<React.StrictMode>` (live at
   * `apps/twin-viewer/src/index.tsx:59`) makes routine on every mount.
   */
  const attemptedOrgSelectionRef = useRef<string | null>(null);

  /**
   * The organisation selected RIGHT NOW, readable from inside an async fetch that was started under a
   * possibly different one. A ref, not the render closure, because that is the whole point: the closure
   * holds the org the request was issued for.
   */
  const currentOrgBIDRef = useRef<string | undefined>(undefined);
  currentOrgBIDRef.current = authMetadata?.orgBID;

  /**
   * ⚠️ THE IN-FLIGHT GUARD MUST BE A REF, AND `isCheckingAccess` IS NOT ONE. `isCheckingAccess` is
   * `useState`, so the fetch effect reads it from the RENDER CLOSURE: `setIsCheckingAccess(true)` only
   * becomes visible to the guard once a re-render commits, and every effect invocation before that
   * commit still sees `false` and starts another request. Measured against a real fixture: 2 concurrent
   * requests on a plain mount, and 3–5 under `<React.StrictMode>` — which is not hypothetical, because
   * `apps/twin-viewer/src/index.tsx:59` wraps the whole app in it. A `useState` value cannot serialise
   * work started inside an effect; only a ref can.
   *
   * `isCheckingAccess` STAYS, for two other jobs: the re-check effect below reads it, and it remains a
   * dependency of the fetch effect so that flipping it back to `false` re-runs the effect — which is how
   * a fetch that was refused as in-flight, or one that failed, gets another go.
   */
  const twinFetchInFlightRef = useRef<boolean>(false);

  /**
   * Bounded retry, per organisation. Keeping `isCheckingAccess` in the fetch effect's deps means a
   * FAILED read is retried when the flag flips back — necessary, because `getOrgTwins` swallows errors
   * and `lastFetchedOrgBIDRef` is only set on success, so nothing else would ever try again and the list
   * would stick at `null` for the whole session. Unbounded, though, that same property is a hot loop
   * against a failing endpoint, so attempts are capped per org and reset when the org changes.
   */
  const twinFetchAttemptsRef = useRef<{ orgBID: string | undefined; attempts: number }>({
    orgBID: undefined,
    attempts: 0,
  });
  const MAX_TWIN_FETCH_ATTEMPTS = 3;

  // Minimum parts needed to extract twin BID from a spotlight BID (e.g., TFN-WMZ from TFN-WMZ-E035)
  const MIN_BID_PARTS_FOR_TWIN_EXTRACTION = 2;

  // Helper function to extract twin BID from asset/area BID
  // Example: TFN-WMZ-E035 -> TFN-WMZ (first two parts)
  const extractTwinBidFromAssetBid = useCallback((bid: string): string | null => {
    const parts = bid.split('-');
    if (parts.length >= MIN_BID_PARTS_FOR_TWIN_EXTRACTION) {
      return `${parts[0]}-${parts[1]}`;
    }
    return null;
  }, []);

  // Clear context function (similar to OrganisationSelector)
  const clearContext = useCallback(() => {
    clearFilter(true);
    setMetrics([]);
    setTwinBId(null);
    setTwin(null);
  }, [clearFilter, setMetrics, setTwinBId, setTwin]);

  // Auto-select organization from spotlight BID if not already selected
  useEffect(() => {
    const selectOrganization = async () => {
      if (!spotlightBid || !isAuthenticated || isSelectingOrg) {
        return;
      }

      const orgBid = extractOrgBidFromSpotlightBid(spotlightBid);
      if (!orgBid) {
        return;
      }

      // If org is already selected and matches, skip
      if (authMetadata?.orgBID === orgBid) {
        return;
      }

      // ONE FIRE, ONE ATTEMPT — see `attemptedOrgSelectionRef`. The key is written BEFORE the
      // first await, so the next effect invocation already sees it whether or not a render has
      // committed. On success this branch stops mattering: `authMetadata.orgBID` is set and the
      // guard above returns first.
      const attemptKey = `${orgBid}#${spotlightFireCount}`;
      if (attemptedOrgSelectionRef.current === attemptKey) {
        return;
      }
      attemptedOrgSelectionRef.current = attemptKey;

      try {
        setIsSelectingOrg(true);
        const accessToken = await getAccessTokenSilently();
        const organisation = await getOrganisationById(Config.middlewareApi, accessToken, orgBid);

        if (organisation) {
          clearContext();

          const newAuthMetadata = {
            orgID: organisation.id,
            orgBID: orgBid,
            orgLongitude: organisation.longitude,
            orgLatitude: organisation.latitude,
            organisation: organisation.name,
            dgraphUrl: organisation.dgraph_url
              ? removeProtocol(organisation.dgraph_url)
              : undefined,
            dgraph_namespace: organisation.dgraph_namespace,
            envName: '',
            environment: '',
            name: user?.name,
            firstName: user?.given_name,
            lastName: user?.family_name,
            email: user?.email,
            tokens: { accessToken: accessToken },
            tsdbUrl: organisation.tsdb_url,
          };
          setAuthMetadata(newAuthMetadata);
          setAppModeWithFilterReset(AppMode.TWIN_SELECTION);

          // Reset accessibleTwins to trigger refetch after org selection
          // (the lastFetchedOrgBIDRef guard alone isn't sufficient — clearing
          // the store value ensures any other consumer sees a stale-free state).
          setAccessibleTwins(null);
          lastFetchedOrgBIDRef.current = undefined;
        } else {
          showNotification(
            `Could not load organization ${orgBid}. Please select it manually.`,
            'error',
            5000,
          );
        }
      } catch (error) {
        console.error('Error selecting organization:', error);
        showNotification(
          `Error loading organization ${orgBid}. Please select it manually.`,
          'error',
          5000,
        );
      } finally {
        setIsSelectingOrg(false);
      }
    };

    selectOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- zustand store setters are stable
  }, [
    spotlightBid,
    // The fire count is a DEPENDENCY, not just a value read inside: re-firing the same bid is a
    // deliberate user retry and must re-enter this effect. `SpotlightStore` documents the count as
    // the trigger and the bid as the payload; this effect used to read only the payload, so a
    // re-click on an org that had failed did nothing at all.
    spotlightFireCount,
    isAuthenticated,
    isSelectingOrg,
    authMetadata?.orgBID,
    extractOrgBidFromSpotlightBid,
    getAllOrganizations,
    getAccessTokenSilently,
    clearContext,
    setAuthMetadata,
    setAppModeWithFilterReset,
    user,
    showNotification,
  ]);

  // Fetch the SELECTED ORGANISATION's twins when authenticated.
  // Also refetch when organization changes to get twins for the new organization
  //
  // ⭐ ORG-SCOPED, DELIBERATELY. This used to call the un-scoped `getTwins` — the middleware's
  // `GET /api/twins` "get ALL twins" route, guarded by login alone — so `accessibleTwins` held the
  // WHOLE ESTATE regardless of which organisation was selected. The effect's own comment above, and
  // the `lastFetchedOrgBIDRef` guard below, have always described a per-organisation fetch; the org
  // was simply never passed. The consequence reached customers: every consumer of this list (the gym
  // picker on a native dashboard, `hasAccessToTwin`, the map spotlight's cross-twin fly-to, the
  // notification twin-name lookup) offered gyms in other organisations, whose data reads the
  // middleware's tenancy guard then correctly refused — so a dashboard could not tell "no data" from
  // "not permitted". Inside the BI area an organisation is already selected, and every twin list
  // shown there must contain exactly that organisation's twins.
  //
  // ⛔ SCOPE NOTE: this changes WHICH ENDPOINT THE FRONTEND CALLS and nothing else. The estate-wide
  // exposure of `GET /api/twins` to any authenticated caller is a separate, ledgered tenancy finding
  // that is NOT touched here — no guard, route or Auth0 behaviour is changed by this commit.
  useEffect(() => {
    const fetchAccessibleTwins = async () => {
      const currentOrgBID = authMetadata?.orgBID;

      if (!isAuthenticated) {
        return;
      }

      // The ONLY in-flight guard, and deliberately so: the ref is written synchronously before the
      // first `await`, so it is already visible to the next effect invocation.
      //
      // ⚠️ `isCheckingAccess` IS NOT CHECKED HERE, on purpose. It is `useState`, read from the render
      // closure, so it still reads `false` until a commit lands — guarding on it let 2–5 concurrent
      // requests through (see `twinFetchInFlightRef` for the measurements). It stays in this effect's
      // dependency array for a DIFFERENT job: flipping it back to `false` is what re-runs the effect and
      // gives a FAILED read its next attempt. The re-check effect further down also reads it, so it does
      // not judge access mid-fetch.
      if (twinFetchInFlightRef.current) {
        return;
      }

      // No organisation selected yet — there is no scoped list to fetch, and the un-scoped
      // estate list is not an acceptable stand-in. Every consumer already treats `null` as
      // "not loaded" and degrades safely: `hasAccessToTwin` returns false (and the spotlight
      // effects gate on `accessibleTwins !== null` before acting on that), the renderer's gym
      // context becomes `undefined` rather than `[]`, and `resolveTarget` falls through to its
      // own pose-fallback chain. The spotlight deep-link path selects the organisation first,
      // so it reaches this fetch with a bid.
      if (!currentOrgBID) {
        return;
      }

      // Skip if we already fetched twins for this org
      if (lastFetchedOrgBIDRef.current === currentOrgBID && accessibleTwins) {
        return;
      }

      // Attempts are counted per organisation, and the counter resets when the org changes so a new
      // selection always gets a fresh budget.
      if (twinFetchAttemptsRef.current.orgBID !== currentOrgBID) {
        twinFetchAttemptsRef.current = { orgBID: currentOrgBID, attempts: 0 };
      }
      if (twinFetchAttemptsRef.current.attempts >= MAX_TWIN_FETCH_ATTEMPTS) {
        // Budget spent. Stop rather than hammer a failing endpoint — the list stays `null`, which every
        // consumer degrades on, and the error was logged on each attempt. Selecting a different
        // organisation resets the budget and tries again.
        return;
      }

      // 🚨 CLEAR THE PREVIOUS ORGANISATION'S LIST BEFORE FETCHING THE NEW ONE. Only the
      // spotlight's own org auto-select resets this; a manual switch through
      // `OrganisationSelector` does not, so without this the window between `orgBID` changing
      // and the new list landing left a NON-NULL list belonging to the OLD organisation while
      // the new one was already selected. Un-scoped that was harmless (the estate list
      // contained everything); org-scoped it is not — `hasAccessToTwin` would answer false for
      // a twin the caller can perfectly well reach and the spotlight would fire a spurious
      // "Access Denied", and the gym picker would offer the previous organisation's gyms.
      // `null` is the state every consumer already reads as "not loaded yet", which is the
      // truth here, and the renderer documents `undefined → [...]` as a normal transition.
      if (lastFetchedOrgBIDRef.current !== currentOrgBID && accessibleTwins) {
        setAccessibleTwins(null);
      }

      try {
        // Claim the in-flight slot SYNCHRONOUSLY, before the first `await`. This is the line that makes
        // the guard above hold; `setIsCheckingAccess` alone never did.
        twinFetchInFlightRef.current = true;
        twinFetchAttemptsRef.current.attempts += 1;
        setIsCheckingAccess(true);
        const accessToken = await getAccessTokenSilently();
        /**
         * RESPONSE-SHAPE RECONCILIATION — ONE WIRE OBJECT, TWO HAND-WRITTEN TYPES THAT DISAGREE.
         *
         * Both middleware routes answer with the SAME `TwinResponseDto[]`, projected from the same
         * `twin` table row: `GET /api/twins` (`TwinsController.findAll`) and
         * `GET /api/organisations/:bid/twins` (`OrganisationsController.getTwins`). So switching
         * endpoints changes WHICH ROWS come back — the point of this change — and not their shape.
         *
         * What differs is how the frontend DESCRIBES that object. `getOrgTwins` declares
         * `AdminTwin[]`; the un-scoped `getTwins` declares `Twin[]`; neither function parses anything,
         * both return `res.data` untouched. Where they disagree, `TwinResponseDto` sides with `Twin`:
         *   • `latitude`/`longitude`/`orientation` — DTO `number | null`; `Twin` `number`; `AdminTwin`
         *     wrongly `string`.
         *   • `camera_start_args` — DTO `Json | null`; `Twin` `string`; `AdminTwin` `CameraSetup`.
         *   • `notification_email` / `notification_mobile_number` / `twin_template_id` /
         *     `nola_location_id` / `deleted_at` — on the DTO and on `Twin`, absent from `AdminTwin`.
         *
         * ⛔ SO THE CAST ADDS FIELDS BACK RATHER THAN LOSING ANY, and nothing is mapped, coerced or
         * dropped: `Twin` is the more faithful description of the identical payload. It is asserted
         * here, at the one call site that needs it, rather than by widening `getOrgTwins` — a generic
         * return type was tried and REJECTED because `TwinMarkers` and `TwinSelector` derive their
         * state types via `ReturnType<typeof getTwins>`, which then infers `unknown[]` and fails
         * `check-types` in `@repo/ui`. Correcting `AdminTwin` itself is the real fix; it has six
         * consumers across three apps and is backlogged, not smuggled into a surgical change.
         *
         * The consumers of this list are already defensive about the difference and say so:
         * `resolveTarget` takes `camera_start_args` as `unknown` through `readCameraStart`, and
         * guards `latitude`/`longitude` with runtime `typeof === 'number'` checks.
         */
        const twins = (await getOrgTwins(
          Config.middlewareApi,
          accessToken,
          currentOrgBID,
        )) as unknown as Twin[] | null;

        // 🚨 COMMIT ONLY IF THIS IS STILL THE SELECTED ORGANISATION. The guard is on IDENTITY, not on
        // arrival order: the question a late response must answer is not "was I superseded?" but "am I
        // the list for the organisation on screen?".
        //
        // ⚠️ AN EARLIER ROUND OF THIS CHANGE OMITTED THIS GUARD, on the argument that the clear above
        // would overwrite a wrong-org list "in the same tick it lands, before any consumer renders it".
        // That argument is FALSE and a reviewer reproduced the bug end to end. `setAccessibleTwins(twins)`
        // and `setIsCheckingAccess(false)` batch into ONE commit, so consumers render — and effects in
        // that same commit run with the wrong list in their closures — before the corrective clear runs.
        // The reachable sequence is A→B→A: B's request is still open when the user returns to A, so B's
        // response commits under A. Observed store trace `"TFN -> NPL-STN"`, and the user got a blocking
        // `window.alert` "You do not have access to twin TFN-WMZ" for a twin sitting in the final list.
        // Worse, that branch sets `spotlightProcessedRef.current = true`, which permanently suppresses
        // the entity selection and fly-to for that spotlight fire — the deep link lands on the twin and
        // then does nothing. Pinned by the A→B→A deep-link test.
        //
        // `lastFetchedOrgBIDRef` is deliberately left untouched on a drop, so the fetch for the org that
        // IS selected still happens.
        if (twins && currentOrgBIDRef.current !== currentOrgBID) {
          console.warn(
            '[useSpotlight] discarding org-scoped twin list for',
            currentOrgBID,
            '— the selected organisation changed to',
            currentOrgBIDRef.current,
            'while the request was in flight',
          );
        } else if (twins) {
          setAccessibleTwins(twins);
          lastFetchedOrgBIDRef.current = currentOrgBID;
        } else {
          // 🚨 FAIL LOUDLY — the `catch` below CANNOT see this. `getOrgTwins` swallows every error and
          // returns `null` ("Silent fail", by name, in its own source), whereas the un-scoped `getTwins`
          // this replaces logged before returning `undefined`. Without this branch the switch would have
          // traded a diagnosable failure for a silent one: no log, no notification, an empty gym picker,
          // and a `hasAccessToTwin` that refuses every twin. That exact silence is how F7 hid — an empty
          // `Config.middlewareApi` makes `createRoutes` build the RELATIVE `api/organisations/<bid>/twins`,
          // axios resolves it against the viewer's own origin, it 404s, and `getOrgTwins` eats it — so the
          // base URL is named here to make that case identifiable from one console line.
          console.error(
            '[useSpotlight] org-scoped twin fetch returned no list for org',
            currentOrgBID,
            '— accessible twins stay unset, so the gym picker will offer only the host twin. ' +
              'Check the middleware base URL is absolute and the route is reachable:',
            Config.middlewareApi,
          );
        }
      } catch (error) {
        console.error('Error fetching accessible twins:', error);
      } finally {
        // Release the in-flight slot BEFORE the state flip, so the re-run that `setIsCheckingAccess`
        // triggers finds the slot free and can retry a failed read within its budget.
        twinFetchInFlightRef.current = false;
        setIsCheckingAccess(false);
      }
    };

    fetchAccessibleTwins();
    // ⚠️ `isCheckingAccess` IS IN THE DEPS ON PURPOSE, even though the real in-flight guard is a ref.
    // Flipping it back to `false` is what re-runs this effect, and that re-run is the only thing that
    // retries a read which FAILED — `getOrgTwins` swallows errors and `lastFetchedOrgBIDRef` is set only
    // on success, so without it the list sticks at `null` for the rest of the session and the picker
    // offers nothing. `twinFetchAttemptsRef` is what stops that retry becoming a hot loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isCheckingAccess, getAccessTokenSilently, authMetadata?.orgBID]);

  // Helper function to check if user has access to a twin
  const hasAccessToTwin = useCallback(
    (twinBidToCheck: string): boolean => {
      if (!accessibleTwins) {
        // If we haven't loaded accessible twins yet, return false to prevent switching
        // This ensures we only switch if we've verified access
        return false;
      }
      return accessibleTwins.some(twin => twin.bid === twinBidToCheck);
    },
    [accessibleTwins],
  );

  // Handle spotlight query parameter and set twin BID if needed (with access check)
  // This runs after organization is selected and accessible twins are loaded
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug(
      '[useSpotlight] twinSelect-effect fired — spotlightBid=',
      spotlightBid,
      'fireCount=',
      spotlightFireCount,
      'lastSeen=',
      lastSeenFireCountRef.current,
      'processed=',
      spotlightProcessedRef.current,
      'twinBId=',
      twinBId,
      'accessibleTwins=',
      accessibleTwins?.length ?? null,
      'orgBID=',
      authMetadata?.orgBID,
    );

    // Reset the processed flag on every new fire (count bump), regardless of
    // whether the BID itself changed.
    if (lastSeenFireCountRef.current !== spotlightFireCount) {
      // eslint-disable-next-line no-console
      console.debug('[useSpotlight] resetting processed flag (fire count bumped)');
      spotlightProcessedRef.current = false;
      lastSeenFireCountRef.current = spotlightFireCount;
    }

    // Only proceed if we have an organization selected
    if (!authMetadata?.orgBID) {
      return;
    }

    // Extract twin BID from spotlight BID if provided
    if (spotlightBid) {
      const inferredTwinBid = extractTwinBidFromAssetBid(spotlightBid);
      const expectedOrgBid = extractOrgBidFromSpotlightBid(spotlightBid);

      // CRITICAL: Ensure the organization context matches the expected organization
      // This prevents a race condition where twinBId is set before authMetadata is updated
      // which would cause the DGraph query to use the wrong organization namespace
      if (expectedOrgBid && authMetadata.orgBID !== expectedOrgBid) {
        // Organization hasn't been switched yet, wait for it
        return;
      }

      if (inferredTwinBid && inferredTwinBid !== twinBId) {
        // Only switch if we have accessible twins loaded and user has access
        if (accessibleTwins !== null) {
          if (hasAccessToTwin(inferredTwinBid)) {
            // Clear the old twin data to ensure proper reload
            // This ensures the 3D scene properly reloads and websocket connections work
            setTwin(null);
            setMetrics([]);
            // Set the new twin BID - this will trigger TwinPage to load the new twin
            // Also update the twin store to match TwinSelector behavior
            // Trigger a refresh to ensure the twin loads properly
            // CP6c: order matters here. setTwinBId auto-clears
            // prefetchedTwinFlightBid as part of its setter, so we must set
            // the marker *after* setTwinBId — otherwise the marker would be
            // cleared immediately and MapCanvas's on-load effect would fire
            // its own redundant flyToTarget on top of our predictive one.
            setTwinBId(inferredTwinBid);
            setSelectedTwinBID(inferredTwinBid);
            setTwinNeedsRefresh(true);
            setPrefetchedTwinFlightBid(inferredTwinBid);
            // Predictive phase-1 dispatch — fire the fly-to-twin immediately
            // (without waiting for loadTwinIntoContext to resolve) so the
            // lateral motion overlaps the data + GLTF load. resolveTarget's
            // cross-twin branch reads the saved camera_start_args from
            // accessibleTwins. If the target twin lacks usable pose data,
            // flyToTarget rejects (UnknownTargetKind) and MapCanvas's
            // existing on-load dispatch picks up after the twin loads.
            // eslint-disable-next-line no-console
            console.debug(
              '[useSpotlight] CP6c predictive flyToTarget(twin) for bid=',
              inferredTwinBid,
            );
            predictiveTwinFlightPromiseRef.current = flyToTarget(inferredTwinBid, {
              chainHandoff: 'before-phase2',
            }).catch(error => {
              if (error instanceof FlightInterrupted) return;
              // eslint-disable-next-line no-console
              console.warn(
                '[useSpotlight] predictive flyToTarget failed; falling back to on-load dispatch',
                inferredTwinBid,
                error,
              );
              setPrefetchedTwinFlightBid(null);
            });
          } else {
            // User doesn't have access to this twin
            alert(
              `Access Denied\n\nYou do not have access to twin ${inferredTwinBid}.\n\nPlease contact your administrator if you believe this is an error.`,
            );
            showNotification(
              `You do not have access to twin ${inferredTwinBid}. Please contact your administrator if you believe this is an error.`,
              'error',
              8000,
            );
            spotlightProcessedRef.current = true;
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- zustand store setters are stable
  }, [
    spotlightBid,
    spotlightFireCount,
    twinBId,
    setTwinBId,
    setTwin,
    setTwinNeedsRefresh,
    setMetrics,
    setSelectedTwinBID,
    extractTwinBidFromAssetBid,
    extractOrgBidFromSpotlightBid,
    hasAccessToTwin,
    showNotification,
    accessibleTwins,
    authMetadata?.orgBID,
  ]);

  // Re-check access once accessible twins are loaded (in case we switched before checking)
  useEffect(() => {
    if (spotlightBid && accessibleTwins !== null && !isCheckingAccess) {
      const inferredTwinBid = extractTwinBidFromAssetBid(spotlightBid);
      if (inferredTwinBid && inferredTwinBid === twinBId && !hasAccessToTwin(inferredTwinBid)) {
        alert(
          `Access Denied\n\nYou do not have access to twin ${inferredTwinBid}.\n\nPlease contact your administrator if you believe this is an error.`,
        );
        showNotification(
          `You do not have access to twin ${inferredTwinBid}. Please contact your administrator if you believe this is an error.`,
          'error',
          8000,
        );
      }
    }
  }, [
    spotlightBid,
    accessibleTwins,
    isCheckingAccess,
    twinBId,
    extractTwinBidFromAssetBid,
    hasAccessToTwin,
    showNotification,
  ]);

  // Process spotlight: Select entity and zoom to it
  useEffect(() => {
    // Infer twin BID from spotlight BID if not already set
    const inferredTwinBid = spotlightBid ? extractTwinBidFromAssetBid(spotlightBid) : null;
    const targetTwinBid = inferredTwinBid || twinBId;

    // eslint-disable-next-line no-console
    console.debug(
      '[useSpotlight] process-effect fired — spotlightBid=',
      spotlightBid,
      'targetTwinBid=',
      targetTwinBid,
      'twin?',
      !!twin,
      'physicalModel?',
      !!twin?.physicalModel,
      'physicalModel.bID=',
      twin?.physicalModel?.bID,
      'allModelsLoaded=',
      allModelsLoaded,
      'loadingBanterRunning=',
      loadingBanterRunning,
      'processed=',
      spotlightProcessedRef.current,
      'lastSeenFireCount=',
      lastSeenFireCountRef.current,
    );

    // Only process spotlight if:
    // 1. We have spotlight BID
    // 2. Twin is loaded
    // 3. All models are loaded
    // 4. We haven't already processed this spotlight
    if (
      spotlightBid &&
      targetTwinBid &&
      twin &&
      twin.physicalModel &&
      allModelsLoaded &&
      !loadingBanterRunning &&
      !spotlightProcessedRef.current
    ) {
      // Verify we're on the correct twin
      if (twin.physicalModel.bID !== targetTwinBid) {
        return; // Wait for correct twin to load
      }

      // Find the entity by bID
      const targetEntity = findEntityWithBID(twin.physicalModel, spotlightBid);

      // eslint-disable-next-line no-console
      console.debug(
        '[useSpotlight] entered process block — targetEntity found?',
        !!targetEntity,
        'bID=',
        targetEntity?.bID,
      );

      if (targetEntity) {
        // Select the entity in the asset panel
        removeFilterByType(FilterType.ENTITY);
        removeFilterByType(FilterType.ASSET);

        const filterPath = createFilterPath(targetEntity.id, twin.physicalModel);
        setFilter(filterPath);

        // Claim the spotlight synchronously so re-renders that fire while
        // we're awaiting phase 1 don't dispatch a duplicate phase 2.
        spotlightProcessedRef.current = true;
        lastSeenFireCountRef.current = spotlightFireCount;

        // When a predictive twin flight (CP6c) is in progress, wait for
        // it to settle before dispatching the entity flight. Otherwise
        // phase 2's animator interrupts phase 1's flyTo with map.stop(),
        // freezing the camera mid-lateral-sweep and producing a visible
        // pause-and-restart at the handoff.
        //
        // Phase 2 is skipped if this spotlight stopped being current while phase 1 flew — a
        // "Show whole twin" reset clears it (activeSpotlightBid → null) and a newer fire bumps
        // the count. Without the check a reset mid-phase-1 is overridden by this flight.
        const predictivePromise = predictiveTwinFlightPromiseRef.current;
        predictiveTwinFlightPromiseRef.current = null;
        const firedCount = spotlightFireCount;
        const phase2 = predictivePromise
          ? chainAfterPredictiveFlight(
              predictivePromise,
              () => flyToTarget(targetEntity.bID, { chainHandoff: 'after-phase1' }),
              () => {
                const s = useSpotlightStore.getState();
                return s.activeSpotlightBid !== null && s.spotlightFireCount === firedCount;
              },
            )
          : flyToTarget(targetEntity.bID);
        phase2.catch(error => {
          if (error instanceof FlightInterrupted) return;
          // eslint-disable-next-line no-console
          console.error('[useSpotlight] flyToTarget failed for', targetEntity.bID, error);
        });
      } else {
        spotlightProcessedRef.current = true;
        lastSeenFireCountRef.current = spotlightFireCount;
      }
    }
  }, [
    spotlightBid,
    spotlightFireCount,
    twin,
    twinBId,
    allModelsLoaded,
    loadingBanterRunning,
    setFilter,
    removeFilterByType,
    extractTwinBidFromAssetBid,
  ]);
};
